import React, { useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { 
  FaMoneyBillWave, FaUniversity, FaGraduationCap, FaBook, FaBuilding, FaIdCard 
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios';
import GenericCrudPage from '../components/GenericCrudPage';
import {
  useColleges,
  useDegrees,
  usePrograms,
  useFeeType,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const FeeCategoryPage = () => {
  const [filterDegree, setFilterDegree] = useState("");
  const [fetchConfig, setFetchConfig] = useState({ degree: 0 });

  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programmes: programs, loading: programsLoading } = usePrograms();
  const { feeTypes, loading: feeTypesLoading } = useFeeType();

  const dropdownsLoading = collegesLoading || degreesLoading || programsLoading || feeTypesLoading;

  const customFetch = async () => {
    const token = Cookies.get('token');
    const url = `${BASE_URL}/master/getFeeCategoryOnDegreeOnly`;

    try {
      const payload = {
        degree: fetchConfig.degree || 0
      };

      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("Fee Category API Response:", res.data);
      return res.data?.details || [];
    } catch (error) {
      console.error("Failed to fetch fee categories:", error);
      toast.error("Could not fetch fee category data.");
      return [];
    }
  };

  const formFields = [
    {
      title: 'Fee Category Information',
      fields: [
        { 
          name: 'exName', 
          label: 'Fee Category Name', 
          icon: FaMoneyBillWave, 
          required: true 
        },
        { 
          name: 'colleges', 
          label: 'College', 
          icon: FaBuilding, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(colleges) ? colleges : [])
            .filter(Boolean)
            .map(c => ({ value: c.id, label: c.exName })) 
        },
        { 
          name: 'degree', 
          label: 'Degree', 
          icon: FaGraduationCap, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(degrees) ? degrees : [])
            .filter(Boolean)
            .map(d => ({ value: d.id, label: d.exName })) 
        },
        { 
          name: 'feetype', 
          label: 'Fee Type', 
          icon: FaMoneyBillWave, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(feeTypes) ? feeTypes : [])
            .filter(Boolean)
            .map(f => ({ value: f.id, label: f.exFeeName })) 
        },
        { 
          name: 'exAbAcDac', 
          label: 'Code (AB/AC/DAC)', 
          icon: FaIdCard 
        },
      ]
    },
    {
      title: 'Program Selection',
      fields: [
        { 
          name: 'programmes', 
          label: 'Programs', 
          icon: FaBook, 
          as: 'select',
          multiple: true,
          renderAsCheckboxes: true,
          colSize: 12,
          options: (Array.isArray(programs) ? programs : [])
            .filter(Boolean)
            .map(p => ({ value: p.id, label: p.exName })),
          helpText: 'Select one or more programs for this fee category'
        },
      ]
    },
    {
      title: 'System Settings',
      fields: [
        { 
          name: 'exIsActive', 
          label: 'Active Status', 
          icon: FaMoneyBillWave, 
          as: 'switch' 
        },
      ]
    }
  ];

  const validateForm = (form) => {
    const errors = {};
    if (!form.exName?.trim()) errors.exName = 'Fee category name is required';
    if (!form.colleges) errors.colleges = 'College is required';
    if (!form.degree) errors.degree = 'Degree is required';
    if (!form.feetype) errors.feetype = 'Fee type is required';
    
    return errors;
  };

  const transformPayload = (form, editId, userId) => {
    const payload = {
      exName: form.exName?.trim() || "",
      degree: Number(form.degree) || 0,
      colleges: Number(form.colleges) || 0,
      feetype: Number(form.feetype) || 0,
      exIsActive: form.exIsActive ? true : false,
    };

    if (form.programmes) {
      if (Array.isArray(form.programmes)) {
        payload.programmes = form.programmes.map(p => Number(p));
      } else if (form.programmes) {
        payload.programmes = [Number(form.programmes)];
      }
    }

    if (form.exAbAcDac?.trim()) {
      payload.exAbAcDac = form.exAbAcDac.trim();
    }

    if (editId) {
      payload.id = editId;
    }

    return payload;
  };

  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/master/getFeeCategoryOnDegreeOnly',
      save: '/master/createFeeCategory',
      update: '/master/updateFeeCategory',
      delete: '/master/deleteFeeCategory' 
    },
    validateForm: validateForm,
    initialFormState: {
      exName: "",
      colleges: "",
      degree: "",
      feetype: "",
      programmes: [],
      exAbAcDac: "",
      exIsActive: true,
    },
    entityName: 'Fee Category',
    fetchMethod: 'CUSTOM',
    customFetch: customFetch,
  };

  const transformToForm = (item) => {
    if (!item) return config.initialFormState;
    
    return {
      exName: item.feename || "",
      colleges: item.collegeid?.toString() || "",
      degree: item.degreeid?.toString() || "",
      feetype: item.feetypeid?.toString() || "",
      programmes: item.programmeId || [],
      exAbAcDac: item.AbAcDac || "",
      exIsActive: item.isactive === true || item.isactive === 1,
    };
  };

  const tableColumns = [
    { 
      key: 'feename', 
      label: 'Fee Category Name', 
      sortable: true 
    },
    { 
      key: 'collegename', 
      label: 'College', 
      sortable: true,
      render: (item) => item.collegename || '-'
    },
    { 
      key: 'degreename', 
      label: 'Degree', 
      sortable: true,
      render: (item) => item.degreename || '-'
    },
    { 
      key: 'feetypename', 
      label: 'Fee Type', 
      sortable: true,
      render: (item) => item.feetypename || '-'
    },
    { 
      key: 'AbAcDac', 
      label: 'Code', 
      sortable: true,
      render: (item) => item.AbAcDac || '-'
    },
  ];

  const csvHeaders = [
    { key: 'feename', label: 'Fee Category Name' },
    { key: 'collegename', label: 'College' },
    { key: 'degreename', label: 'Degree' },
    { key: 'feetypename', label: 'Fee Type' },
    { key: 'AbAcDac', label: 'Code' },
    { 
      key: 'isactive', 
      label: 'Status', 
      render: (item) => item.isactive ? 'Active' : 'Inactive' 
    }
  ];

  const getViewFields = (item) => {
    if (!item) return [];
    
    const programNames = Array.isArray(item.programmename) 
      ? item.programmename.join(', ') 
      : item.programmename || '-';
    
    return [
      { label: 'Fee Category Name', value: item.feename, icon: FaMoneyBillWave },
      { label: 'College', value: item.collegename, icon: FaBuilding },
      { label: 'Degree', value: item.degreename, icon: FaGraduationCap },
      { label: 'Fee Type', value: item.feetypename, icon: FaMoneyBillWave },
      { label: 'Programs', value: programNames, icon: FaBook },
      { label: 'Code', value: item.AbAcDac || '-', icon: FaIdCard },
      { 
        label: 'Status', 
        value: item.isactive ? 'Active' : 'Inactive', 
        icon: FaMoneyBillWave 
      },
    ];
  };

  const handleClearFilters = () => {
    setFilterDegree('');
    setFetchConfig({ degree: 0 });
  };

  return (
    <Container fluid className="py-4">
      {dropdownsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dropdown data...</span>
          </div>
          <p className="mt-3 text-muted">Loading form data...</p>
        </div>
      ) : (
        <>
          <Row className="mb-4 align-items-end">
            <Col md={6}>
              <Form.Label>Filter by Degree</Form.Label>
              <Form.Select
                value={filterDegree}
                onChange={(e) => {
                  const newDegree = e.target.value;
                  setFilterDegree(newDegree);
                  setFetchConfig({ degree: Number(newDegree) || 0 });
                }}
              >
                <option value="">All Degrees</option>
                {(Array.isArray(degrees) ? degrees : [])
                  .filter(Boolean)
                  .map((d) => (
                    <option key={d.id} value={d.id}>{d.exName}</option>
                  ))}
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex justify-content-start">
              <Button variant="outline-secondary" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </Col>
          </Row>

          <GenericCrudPage
            key={JSON.stringify(fetchConfig)}
            title="Fee Category"
            pluralTitle="Fee Categories"
            config={config}
            formFields={formFields}
            tableColumns={tableColumns}
            emptyIcon={FaMoneyBillWave}
            searchPlaceholder="Search by fee category name..."
            csvHeaders={csvHeaders}
            csvFilename="fee_categories.csv"
            transformPayload={transformPayload}
            transformToForm={transformToForm}
            getViewFields={getViewFields}
          />
        </>
      )}
    </Container>
  );
};

export default FeeCategoryPage;